// Blog.js - Public blog listing page
// SEO-optimized page displaying all published blog posts with pagination
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import SEO from '../components/SEO';
import PageHero from '../components/page/PageHero';
import heroImage from '../assets/blog/hero-reading.webp';

const Blog = () => {
    // --- State Variables ---
    const [posts, setPosts] = useState([]); // Array of blog posts
    const [loading, setLoading] = useState(true); // Loading state for initial fetch
    const [loadingMore, setLoadingMore] = useState(false); // Loading state for pagination
    const [error, setError] = useState(null); // Error message
    const [pagination, setPagination] = useState({ // Pagination state
        currentPage: 1,
        totalPages: 1,
        totalPosts: 0,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [selectedCategory, setSelectedCategory] = useState(null); // Category filter
    const [categories, setCategories] = useState([]); // Available categories

    // --- Theming (consistent with site) ---
    const textPrimary = "text-silver-light";
    const textSecondary = "text-silver-medium/80";

    // --- Intersection Observer for Animations ---
    // The hero no longer needs an observer — <PageHero /> renders immediately
    // rather than fading in, so nothing above the fold waits on one.
    const [contentRef, contentInView] = useInView({ triggerOnce: true, threshold: 0.1 });

    // --- Fetch Blog Posts ---
    const fetchPosts = useCallback(async (page = 1, category = null) => {
        console.log(`[Blog] Fetching posts - page: ${page}, category: ${category}`);
        
        // Set appropriate loading state
        if (page === 1) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        setError(null);

        try {
            // Build query params
            const params = new URLSearchParams({ page, limit: 9 });
            if (category) {
                params.append('category', category);
            }

            // Fetch posts from API
            const res = await axios.get(`/api/blog/published?${params.toString()}`);
            console.log('[Blog] API response:', res.data);

            if (res.data && res.data.success) {
                // If page 1, replace posts; otherwise append
                if (page === 1) {
                    setPosts(res.data.posts);
                } else {
                    setPosts(prev => [...prev, ...res.data.posts]);
                }
                setPagination(res.data.pagination);
            } else {
                throw new Error(res.data.message || 'Failed to fetch posts');
            }
        } catch (err) {
            console.error('[Blog] Fetch error:', err);
            // Message only — the error UI supplies its own heading, so the old
            // "Error loading blog posts: ..." prefix just said it twice.
            setError(err.message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    // --- Fetch Categories ---
    const fetchCategories = useCallback(async () => {
        console.log('[Blog] Fetching categories...');
        try {
            const res = await axios.get('/api/blog/categories');
            if (res.data && res.data.success) {
                setCategories(res.data.categories);
                console.log('[Blog] Categories loaded:', res.data.categories);
            }
        } catch (err) {
            console.error('[Blog] Category fetch error:', err);
        }
    }, []);

    // --- Initial Data Fetch ---
    useEffect(() => {
        console.log('[Blog] Initial mount - fetching data');
        fetchPosts(1, selectedCategory);
        fetchCategories();
    }, [fetchPosts, fetchCategories, selectedCategory]);

    // --- Handle Category Filter ---
    const handleCategoryChange = (category) => {
        console.log('[Blog] Category changed to:', category);
        setSelectedCategory(category);
        fetchPosts(1, category);
    };

    // --- Handle Load More ---
    const handleLoadMore = () => {
        console.log('[Blog] Loading more posts...');
        fetchPosts(pagination.currentPage + 1, selectedCategory);
    };

    // --- Format Date ---
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // --- SEO Schema for Blog Listing ---
    const blogListSchema = {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "Riviera Tech Solutions Blog",
        "description": "Insights on web development, technology trends, and digital solutions from Riviera Tech Solutions.",
        "url": "https://rivieratech.solutions/blog",
        "publisher": {
            "@type": "Organization",
            "name": "Riviera Tech Solutions",
            "logo": {
                "@type": "ImageObject",
                "url": "https://rivieratech.solutions/RTSLOGO.png"
            }
        }
    };

    // --- Loading Skeleton Component ---
    const PostSkeleton = () => (
        <div className="overflow-hidden rounded-lg border border-gray-800 bg-off-black animate-pulse">
            <div className="h-48 bg-gray-800/40"></div>
            <div className="p-6">
                <div className="h-4 bg-gray-800/50 rounded w-1/4 mb-3"></div>
                <div className="h-6 bg-gray-800/50 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-gray-800/50 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-800/50 rounded w-2/3 mb-4"></div>
                <div className="flex justify-between">
                    <div className="h-3 bg-gray-800/50 rounded w-20"></div>
                    <div className="h-3 bg-gray-800/50 rounded w-16"></div>
                </div>
            </div>
        </div>
    );

    // --- Blog Post Card Component ---
    // Card language matches every other card on the site: off-black on a
    // gray-800 hairline, moss on hover. It was gray-900/60 + gray-800/50 +
    // rounded-xl, which belonged to nothing else.
    const PostCard = ({ post }) => (
        <Link
            to={`/blog/${post.slug}`}
            className="group flex flex-col overflow-hidden rounded-lg border border-gray-800 bg-off-black transition-colors duration-300 hover:border-moss/60"
        >
            {/* Featured Image */}
            <div className="h-48 overflow-hidden bg-black">
                {post.featuredImage ? (
                    <img 
                        src={post.featuredImage} 
                        alt={post.featuredImageAlt || post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                    />
                ) : (
                    /* No featured image. A near-black block with a small grey
                       icon read as six identical holes down the grid, so the
                       fallback carries the brand instead: a moss wash with the
                       wordmark initials. Same height as a real image, so the
                       grid stays even when only some posts have one. */
                    <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-gradient-to-br from-moss-deep/50 via-moss-night to-black">
                        <span
                            aria-hidden="true"
                            className="font-serif text-5xl font-medium text-moss/30 transition-transform duration-500 group-hover:scale-110"
                        >
                            RTS
                        </span>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-6 flex-1 flex flex-col">
                {/* Category Badge */}
                {post.category && (
                    <span className="inline-block px-2.5 py-1 border border-moss/50 text-moss-light text-xs font-medium rounded-md mb-3 self-start">
                        {post.category}
                    </span>
                )}

                {/* Title */}
                <h2 className={`mb-2 font-serif text-xl font-medium leading-snug ${textPrimary} group-hover:text-moss-light transition-colors line-clamp-2`}>
                    {post.title}
                </h2>

                {/* Excerpt */}
                <p className={`text-sm ${textSecondary} mb-4 flex-1 line-clamp-3`}>
                    {post.excerpt || 'Read more about this topic...'}
                </p>

                {/* Meta Info */}
                <div className="mt-auto flex items-center justify-between border-t border-gray-800 pt-4 text-xs text-silver-dark">
                    <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                    <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {post.readingTime || 1} min read
                    </span>
                </div>
            </div>
        </Link>
    );

    return (
        <div className="bg-black min-h-screen font-sans">
            {/* SEO Component */}
            <SEO
                title="Custom Web Development Blog | Websites, Web Apps & Ecommerce Guides"
                description="Guides for business owners planning custom websites, web application development, and ecommerce website development. Learn pricing, stack choices, SEO strategy, and delivery best practices."
                keywords="custom web development blog, custom web development cost, ecommerce website developer for small business, benefits of custom web applications, choosing development stack, custom website guide"
                url="/blog"
                type="website"
                schema={blogListSchema}
            />

            <PageHero
                eyebrow="Blog"
                title="Our Blog"
                subtitle="Insights, tutorials, and updates from Riviera Tech Solutions. Stay informed about the latest in web development and technology."
                backgroundImage={heroImage}
            />

            {/* Category Filter */}
            {categories.length > 0 && (
                <section className="border-b border-gray-800 py-6 bg-black">
                    <div className="container mx-auto px-4 sm:px-6">
                        <div className="flex flex-wrap justify-center gap-2">
                            <button
                                onClick={() => handleCategoryChange(null)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                    selectedCategory === null
                                        ? 'border border-moss bg-moss text-black'
                                        : 'border border-gray-700 text-silver-medium hover:border-moss/60 hover:text-silver-light'
                                }`}
                            >
                                All Posts
                            </button>
                            {categories.map(cat => (
                                <button
                                    key={cat._id}
                                    onClick={() => handleCategoryChange(cat._id)}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                        selectedCategory === cat._id
                                            ? 'border border-moss bg-moss text-black'
                                            : 'border border-gray-700 text-silver-medium hover:border-moss/60 hover:text-silver-light'
                                    }`}
                                >
                                    {cat._id} ({cat.count})
                                </button>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Blog Posts Grid */}
            <section
                ref={contentRef}
                className={`py-16 md:py-20 transition-all duration-1000 transform ${contentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
            >
                <div className="container mx-auto px-4 sm:px-6">
                    {/* Error and "no posts yet" are mutually exclusive. They used
                        to render together — a fetch failure showed the red error
                        banner AND "No blog posts yet / Check back soon for new
                        content!", which tells the reader two different stories
                        and makes a broken API look like an empty blog. */}
                    {error ? (
                        <div className="mx-auto max-w-xl rounded-lg border border-red-700/40 bg-red-900/15 px-6 py-8 text-center">
                            <svg className="mx-auto mb-4 h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h2 className={`mb-2 font-serif text-xl font-medium ${textPrimary}`}>
                                We could not load the posts
                            </h2>
                            <p className={`${textSecondary} mb-6 text-sm`}>{error}</p>
                            {/* Retries the fetch directly. Going via
                                handleCategoryChange(selectedCategory) would set
                                the category to the value it already holds, React
                                would bail out of the re-render, and the button
                                would do nothing at all. */}
                            <button
                                onClick={() => fetchPosts(1, selectedCategory)}
                                className="rounded-md bg-moss px-6 py-2.5 text-sm font-medium text-black transition-colors hover:bg-moss-light"
                            >
                                Try again
                            </button>
                        </div>
                    ) : loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[...Array(6)].map((_, i) => (
                                <PostSkeleton key={i} />
                            ))}
                        </div>
                    ) : posts.length > 0 ? (
                        <>
                            {/* Posts Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {posts.map(post => (
                                    <PostCard key={post._id} post={post} />
                                ))}
                            </div>

                            {/* Load More Button */}
                            {pagination.hasNextPage && (
                                <div className="text-center mt-10">
                                    <button
                                        onClick={handleLoadMore}
                                        disabled={loadingMore}
                                        className="rounded-md border border-moss/60 px-8 py-3 font-medium text-silver-light transition-colors duration-300 hover:border-moss hover:bg-moss hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {loadingMore ? (
                                            <span className="flex items-center">
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Loading...
                                            </span>
                                        ) : (
                                            `Load More (${pagination.totalPosts - posts.length} remaining)`
                                        )}
                                    </button>
                                </div>
                            )}

                            {/* Pagination Info */}
                            <div className="mt-6 text-center text-sm text-silver-dark">
                                Showing {posts.length} of {pagination.totalPosts} posts
                            </div>
                        </>
                    ) : (
                        /* Empty State */
                        <div className="mx-auto max-w-xl rounded-lg border border-gray-800 bg-off-black px-6 py-12 text-center">
                            <svg className="mx-auto mb-4 h-12 w-12 text-moss-deep" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                            </svg>
                            <h2 className={`mb-2 font-serif text-xl font-medium ${textPrimary}`}>No blog posts yet</h2>
                            <p className={`${textSecondary} text-sm`}>
                                {selectedCategory
                                    ? `No posts found in this category. Try selecting a different category.`
                                    : `Check back soon for new content!`
                                }
                            </p>
                            {selectedCategory && (
                                <button
                                    onClick={() => handleCategoryChange(null)}
                                    className="mt-6 rounded-md bg-moss px-6 py-2.5 text-sm font-medium text-black transition-colors hover:bg-moss-light"
                                >
                                    View All Posts
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* Closing CTA */}
            <section className="py-20 bg-moss-night border-t border-moss-deep/40">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mx-auto text-center">
                        <h2 className={`mb-4 font-serif text-2xl md:text-3xl font-medium ${textPrimary}`}>
                            Want More Insights?
                        </h2>
                        <p className={`${textSecondary} mb-8`}>
                            Get in touch to discuss how we can help transform your digital presence.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-block rounded-md bg-moss px-8 py-3 text-lg font-medium text-black transition-colors duration-300 hover:bg-moss-light"
                        >
                            Contact Us
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Blog;
